import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Trans, useTranslation } from '@clofo/i18n'
import { clofoApi } from '@clofo/data/api'
import { Alert, AlertDescription, AlertTitle } from '@clofo/ui/alert'
import { Button } from '@clofo/ui/button'
import { Checkbox } from '@clofo/ui/checkbox'
import { FormCheckbox, FormInput } from '@clofo/ui/controlled-field'
import { Field, FieldError, FieldLabel } from '@clofo/ui/field'
import { IceIcon } from '@clofo/ui/ice-icon'
import { SubmitButton } from '@clofo/ui/submit-button'
import { getRedirectTo } from '../lib/redirect'
import { PasswordFields, passwordFieldsShape, serverPasswordErrorMessage } from './password-fields'

const TERMS_OF_USE_URL = 'https://imanage.com/about/terms-of-service/'

export const RegistrationForm = ({
  activationKey,
  email,
}: {
  activationKey: string
  email: string
}) => {
  const { t } = useTranslation()
  const [showSubmitError, setShowSubmitError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const schema = z
    .object({
      firstName: z
        .string()
        .min(1, { error: t('login.passwordFields.provideValue') })
        .max(100, { error: t('login.registration.nameMaxLength') }),
      lastName: z
        .string()
        .min(1, { error: t('login.passwordFields.provideValue') })
        .max(100, { error: t('login.registration.nameMaxLength') }),
      ...passwordFieldsShape(t),
      subscribeToUpdates: z.boolean(),
      acceptTerms: z.boolean().refine(value => value, {
        error: t('login.registration.requireAcceptTerms'),
      }),
    })
    .refine(v => v.newPassword1 === v.newPassword2, {
      error: t('login.passwordFields.mismatch'),
      path: ['newPassword2'],
    })
  type Values = z.infer<typeof schema>

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      newPassword1: '',
      newPassword2: '',
      subscribeToUpdates: false,
      acceptTerms: false,
    },
  })

  const onSubmit = async (values: Values) => {
    setShowSubmitError(false)
    setIsSubmitting(true)
    try {
      await clofoApi.users.completeRegistration(activationKey, {
        first_name: values.firstName,
        last_name: values.lastName,
        new_password1: values.newPassword1,
        new_password2: values.newPassword2,
        subscribe_to_updates: values.subscribeToUpdates,
        accept_terms: values.acceptTerms,
      })
      // Legacy parity: auto-log-in with the just-created password, then hand off to the target
      // app. TODO: accounts with tfa_status enabled/enforced should route to the TFA pages once
      // those exist in this app.
      await clofoApi.auth.loginWithPassword({
        email,
        password: values.newPassword1,
        remember: false,
      })
      window.location.href = getRedirectTo()
    } catch (error) {
      setIsSubmitting(false)
      const passwordMessage = serverPasswordErrorMessage(t, error)
      if (passwordMessage) {
        form.setError('newPassword1', { message: passwordMessage })
        return
      }
      setShowSubmitError(true)
    }
  }

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className='flex flex-col w-full'
      autoComplete='off'
    >
      {showSubmitError && (
        <Alert variant='error' className='mb-6'>
          <AlertTitle className='flex items-center justify-between'>
            {t('login.registration.errorAlertTitle')}
            <Button
              type='button'
              variant='icon'
              aria-label={t('login.reset.dismissError')}
              onClick={() => setShowSubmitError(false)}
            >
              <IceIcon name='close-delete-x' />
            </Button>
          </AlertTitle>
          <AlertDescription>
            <p>
              <Trans
                t={t}
                i18nKey='login.registration.errorAlertDescription'
                components={{
                  contact: <a className='underline' href='mailto:support@closingfolders.com' />,
                }}
              />
            </p>
          </AlertDescription>
        </Alert>
      )}

      <FormInput
        control={form.control}
        name='firstName'
        label={t('login.registration.firstName')}
        isRequired
        autoComplete='off'
        className={{ field: 'max-w-md mb-6', input: 'h-12' }}
      />

      <FormInput
        control={form.control}
        name='lastName'
        label={t('login.registration.lastName')}
        isRequired
        autoComplete='off'
        className={{ field: 'max-w-md mb-6', input: 'h-12' }}
      />

      <PasswordFields control={form.control} />

      <FormCheckbox
        control={form.control}
        name='subscribeToUpdates'
        label={t('login.registration.subscribe')}
        className={{ field: 'mt-6' }}
      />

      {/* Custom checkbox field: the label embeds a link, which FormCheckbox's string label can't
          express. Structure mirrors FormCheckbox (Checkbox + FieldLabel + FieldError). */}
      <Controller
        name='acceptTerms'
        control={form.control}
        render={({ field, fieldState }) => (
          <Field className='gap-2 mt-3' data-invalid={fieldState.invalid}>
            {/* Checkbox + label on one row; the validation error renders below the row. */}
            <div className='flex items-center gap-2'>
              <Checkbox
                id={field.name}
                checked={field.value}
                onCheckedChange={field.onChange}
                aria-required={true}
                aria-invalid={fieldState.invalid}
              />
              <FieldLabel htmlFor={field.name} className='cursor-pointer' isRequired>
                <span>
                  <Trans
                    t={t}
                    i18nKey='login.registration.acceptTerms'
                    components={{
                      terms: (
                        <a
                          href={TERMS_OF_USE_URL}
                          target='_blank'
                          rel='noreferrer noopener'
                          className='text-primary underline font-semibold'
                          // Follow the link without toggling the surrounding checkbox label.
                          onClick={e => e.stopPropagation()}
                        />
                      ),
                    }}
                  />
                </span>
              </FieldLabel>
            </div>
            {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
          </Field>
        )}
      />

      <hr className='my-6 border-secondary' />

      <SubmitButton type='submit' size='lg' className='w-fit' isSubmitting={isSubmitting}>
        {t('login.registration.submit')}
      </SubmitButton>
    </form>
  )
}
